import Dynamicore, { SERVICES } from 'services/dynamicore';
import { getSettings } from 'utils/settings';

import { STEP } from './constants';

export const getRequestFormId = () => Number(getSettings('providers.dynamicore.forms.request_form_id', 694));
export const REQUEST_STATUS = {
  PENDING: 'Pending',
  OBSERVATION: 'Observation',
  CONFIRMED: 'Confirmed',
  AUTHORIZED: 'Authorized',
  COMPLETED: 'Completed',
};

const ACTIVE_REQUEST_STORAGE_KEY = 'activeRequestId';
const REQUESTS_CACHE_TTL_MS = 1200;
const requestsByClientCache = new Map();
const inFlightRequestsByClient = new Map();

const normalizeText = (value) => String(value ?? '').trim().toLowerCase();

const normalizeRequestStep = (step) => {
  if ([STEP.SIMULATOR, STEP.SIMULATOR_RESULTS].includes(step)) return STEP.LOCATION;
  if (step === STEP.RELATIONSHIPS) return STEP.STAKEHOLDERS;
  return step || STEP.LOCATION;
};

const toDateIso = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return new Date().toISOString();
  }
  return date.toISOString();
};

const clone = (value) => JSON.parse(JSON.stringify(value));

const parseNumericValue = (value) => {
  if (value === null || value === undefined || value === '') return null;

  const parsed = Number(
    String(value)
      .replace(/[%,$\s]/g, '')
      .replace(/,/g, ''),
  );

  return Number.isFinite(parsed) ? parsed : null;
};

const getPreProposalAmount = (pii = {}) => {
  const annualIncome = parseNumericValue(pii?.ingresos_anuales);
  if (!Number.isFinite(annualIncome)) {
    return null;
  }

  return annualIncome * 0.2;
};

const getRequestsCacheKey = (clientId) => Number(clientId || 0);

const getCachedRequestsByClient = (clientId) => {
  const key = getRequestsCacheKey(clientId);
  if (!key) return null;

  const cached = requestsByClientCache.get(key);
  if (!cached) return null;

  if (cached.expiresAt < Date.now()) {
    requestsByClientCache.delete(key);
    return null;
  }

  return clone(cached.value);
};

const setCachedRequestsByClient = (clientId, requests) => {
  const key = getRequestsCacheKey(clientId);
  if (!key) return;

  requestsByClientCache.set(key, {
    expiresAt: Date.now() + REQUESTS_CACHE_TTL_MS,
    value: Array.isArray(requests) ? requests : [],
  });
};

const clearCachedRequestsByClient = (clientId) => {
  const key = getRequestsCacheKey(clientId);
  if (!key) return;
  requestsByClientCache.delete(key);
};

export const getActiveRequestId = () => localStorage.getItem(ACTIVE_REQUEST_STORAGE_KEY) || '';

export const setActiveRequestId = (requestId) => {
  if (!requestId) {
    localStorage.removeItem(ACTIVE_REQUEST_STORAGE_KEY);
    return;
  }
  localStorage.setItem(ACTIVE_REQUEST_STORAGE_KEY, String(requestId));
};

export const getRequestsByClient = async (clientId) => {
  const key = getRequestsCacheKey(clientId);
  if (!key) return [];

  const cachedRequests = getCachedRequestsByClient(key);
  if (cachedRequests) {
    return cachedRequests;
  }

  const inFlightRequest = inFlightRequestsByClient.get(key);
  if (inFlightRequest) {
    return clone(await inFlightRequest);
  }

  const requestPromise = Dynamicore(SERVICES.NOTIFICATIONS_FORMS_DATA)
    .get({
      client: key,
      enabled: 1,
    }, false, true)
    .then(({ values = [] }) => {
      const normalizedValues = Array.isArray(values) ? values : [];
      setCachedRequestsByClient(key, normalizedValues);
      return normalizedValues;
    })
    .finally(() => {
      inFlightRequestsByClient.delete(key);
    });

  inFlightRequestsByClient.set(key, requestPromise);

  return clone(await requestPromise);
};

export const resolveActiveRequest = (requests = []) => {
  if (!Array.isArray(requests) || requests.length === 0) {
    setActiveRequestId('');
    return null;
  }

  const storedId = getActiveRequestId();
  const byStoredId = requests.find((item) => String(item?.id) === String(storedId));
  if (byStoredId) {
    return byStoredId;
  }

  const firstPending = requests.find((item) => normalizeText(item?.data?.estatus) !== normalizeText(REQUEST_STATUS.COMPLETED));
  const next = firstPending || requests[0];
  setActiveRequestId(next?.id);

  return next;
};

export const buildRequestDataFromPeople = ({
  existingData = {},
  pii = {},
  explicitStep,
  explicitStatus,
} = {}) => {
  const nowIso = toDateIso();
  const amount = pii?.monto_del_credito_que_buscas;
  const preProposalAmount = getPreProposalAmount(pii);
  const estimatedAmount = preProposalAmount ?? pii?.monto_estimado;
  const term = pii?.plazo_deseado_meses;

  return {
    ...existingData,
    fecha_de_solicitud: existingData?.fecha_de_solicitud || nowIso,
    ultima_actualizacion: nowIso,
    id_de_solicitud: existingData?.id_de_solicitud || `SOL-${Date.now()}`,
    producto: existingData?.producto || 'Solicitud de credito',
    estatus: explicitStatus || existingData?.estatus || REQUEST_STATUS.PENDING,
    step: normalizeRequestStep(explicitStep || pii?.step || existingData?.step),
    monto_del_credito_que_buscas: amount ?? existingData?.monto_del_credito_que_buscas ?? '0',
    ingresos_anuales: pii?.ingresos_anuales ?? existingData?.ingresos_anuales ?? '0',
    monto_estimado: estimatedAmount ?? existingData?.monto_estimado ?? '0',
    plazo_deseado_meses: term ?? existingData?.plazo_deseado_meses ?? '6',
  };
};

export const createRequestForClient = async ({
  clientId,
  pii = {},
} = {}) => {
  if (!clientId) {
    throw new Error('No client id provided for request creation.');
  }

  const payload = {
    form_id: String(getRequestFormId()),
    client: Number(clientId),
    enabled: 1,
    data: buildRequestDataFromPeople({
      pii,
      explicitStep: pii?.step || STEP.LOCATION,
      explicitStatus: REQUEST_STATUS.PENDING,
    }),
  };

  const created = await Dynamicore(SERVICES.NOTIFICATIONS_FORMS_DATA).post(payload);
  clearCachedRequestsByClient(clientId);
  if (created?.id) {
    setActiveRequestId(created.id);
  }

  return created;
};

export const updateRequestRecord = async ({
  request,
  clientId,
  pii,
  step,
  status,
} = {}) => {
  const requestId = request?.id || getActiveRequestId();
  if (!requestId) return null;

  const resolvedClientId = Number(clientId || request?.client || 0);
  if (!resolvedClientId) return null;

  const payload = {
    id: String(requestId),
    form_id: String(request?.form_id || getRequestFormId()),
    client: resolvedClientId,
    enabled: Number(request?.enabled || 1),
    data: buildRequestDataFromPeople({
      existingData: request?.data,
      pii,
      explicitStep: step,
      explicitStatus: status,
    }),
  };

  const updated = await Dynamicore(SERVICES.NOTIFICATIONS_FORMS_DATA).put(payload);
  clearCachedRequestsByClient(resolvedClientId);
  if (updated?.id) {
    setActiveRequestId(updated.id);
  }

  return updated;
};

export const getStepFromRequest = (request) => normalizeRequestStep(request?.data?.step);

export const getStatusLabel = (status) => {
  const normalized = normalizeText(status);
  if (normalized === normalizeText(REQUEST_STATUS.CONFIRMED)) return 'A confirmar';
  if (normalized === normalizeText(REQUEST_STATUS.AUTHORIZED)) return 'Autorizado';
  if (normalized === normalizeText(REQUEST_STATUS.OBSERVATION)) return 'En revisión';
  if (normalized === normalizeText(REQUEST_STATUS.COMPLETED)) return 'Completado';
  return 'Pendiente';
};

export const isCompletedRequest = (request) => normalizeText(request?.data?.estatus) === normalizeText(REQUEST_STATUS.COMPLETED);
export const isPendingRequest = (request) => normalizeText(request?.data?.estatus) === normalizeText(REQUEST_STATUS.PENDING);
export const isObservationRequest = (request) => normalizeText(request?.data?.estatus) === normalizeText(REQUEST_STATUS.OBSERVATION);
export const isConfirmedRequest = (request) => normalizeText(request?.data?.estatus) === normalizeText(REQUEST_STATUS.CONFIRMED);
export const isAuthorizedRequest = (request) => normalizeText(request?.data?.estatus) === normalizeText(REQUEST_STATUS.AUTHORIZED);
